import { createFileRoute } from "@tanstack/react-router";
import Link from "src/components/common/Link/Link";
import Typography from "src/components/common/Typography/Typography";
import { CONTACT_EMAIL } from "src/lib/FrontendConstants";
import { metadataHead } from "src/lib/Metadata";
import { useExtracted } from "#i18n";

// Plain SSR: the page is short, so a server component would cost more (an RSC request on every
// client-side visit) than the little JavaScript it saves.
export const Route = createFileRoute("/_policies/data-request")({
  head: () =>
    metadataHead({
      title: "Your data",
      description:
        "Find out how to download your CalmLens data, delete your account, and request help with your data rights.",
      alternates: { canonical: "/data-request" },
    }),
  component: DataRequestPage,
});

function DataRequestPage() {
  const translateMessage = useExtracted();
  return (
    <div className={"flex flex-col gap-6"}>
      <Typography as="h1" variant="heading2">
        {translateMessage("Your data")}
      </Typography>

      <section className={"flex flex-col gap-2"}>
        <Typography as="h2" variant="heading4">
          {translateMessage("Download a copy of your data")}
        </Typography>
        <ol className={"list-decimal ms-6 gap-2 leading-loose"}>
          <li>
            {translateMessage("Sign in and go to your")}{" "}
            <Link href={"/portal/profile"} variant={"hyperlink"}>
              {translateMessage("profile page")}
            </Link>
          </li>
          <li>
            {translateMessage(
              "Under Your data, press Download data. You'll get your account and project data as a JSON file.",
            )}
          </li>
        </ol>
      </section>

      <section className={"flex flex-col gap-2"}>
        <Typography as="h2" variant="heading4">
          {translateMessage("Delete your account")}
        </Typography>
        <ol className={"list-decimal ms-6 gap-2 leading-loose"}>
          <li>
            {translateMessage("Sign in and go to your")}{" "}
            <Link href={"/portal/profile"} variant={"hyperlink"}>
              {translateMessage("profile page")}
            </Link>
          </li>
          <li>{translateMessage("Under Your data, press Delete account and confirm.")}</li>
        </ol>
        <Typography variant="bodySmall" tone="muted">
          {translateMessage(
            "Deleting your account deletes the projects you own, including content other team members uploaded to them. Content under a safety hold and its incident records are kept. See the Privacy Policy for details.",
          )}
        </Typography>
      </section>

      <section className={"flex flex-col gap-2"}>
        <Typography as="h2" variant="heading4">
          {translateMessage("Other requests")}
        </Typography>
        <Typography>
          {translateMessage(
            "To make another privacy request, or if you can't sign in, email us at",
          )}{" "}
          <Link href={`mailto:${CONTACT_EMAIL}`} variant={"hyperlink"}>
            {CONTACT_EMAIL}
          </Link>
          {translateMessage(". We may need to verify your identity before we act on a request.")}
        </Typography>
        <Typography>
          {translateMessage("Read our")}{" "}
          <Link href={"/privacy-policy"} variant={"hyperlink"}>
            {translateMessage("Privacy Policy")}
          </Link>{" "}
          {translateMessage("for more about your rights.")}
        </Typography>
      </section>
    </div>
  );
}
